// 系统基本的常量/枚举

// ============ 枚举定义 ============

// ============ 常量配置 ============
export const PROMO_CODE_KEY = "promoCode";
export const SUPPLY_MALL_PRESELECT_CATEGORY_ID_KEY = "category_id";
// ============ 精度计算工具函数 ============

/**
 * 获取数字的小数位数
 * @param num 数字
 * @returns 小数位数
 */
function getDecimalPlaces(num: number): number {
  const str = String(num);
  const decimalIndex = str.indexOf(".");
  return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1;
}

/**
 * 精确加法（避免浮点数精度丢失）
 * @param num1 加数1
 * @param num2 加数2
 * @returns 精确的和
 * @example
 * add(0.1, 0.2) // 0.3
 * add(0.07, 0.01) // 0.08
 */
export function add(num1: number, num2: number): number {
  const places1 = getDecimalPlaces(num1);
  const places2 = getDecimalPlaces(num2);
  const maxPlaces = Math.max(places1, places2);
  const factor = Math.pow(10, maxPlaces);
  return (Math.round(num1 * factor) + Math.round(num2 * factor)) / factor;
}

/**
 * 精确减法（避免浮点数精度丢失）
 * @param num1 被减数
 * @param num2 减数
 * @returns 精确的差
 * @example
 * subtract(0.3, 0.1) // 0.2
 * subtract(1.0, 0.7) // 0.3
 */
export function subtract(num1: number, num2: number): number {
  const places1 = getDecimalPlaces(num1);
  const places2 = getDecimalPlaces(num2);
  const maxPlaces = Math.max(places1, places2);
  const factor = Math.pow(10, maxPlaces);
  return (Math.round(num1 * factor) - Math.round(num2 * factor)) / factor;
}

/**
 * 精确乘法（避免浮点数精度丢失）
 * @param num1 乘数1
 * @param num2 乘数2
 * @returns 精确的乘积结果
 * @example
 * multiply(0.1, 0.2) // 0.02
 * multiply(19.9, 100) // 1990
 */
export function multiply(num1: number, num2: number): number {
  const places1 = getDecimalPlaces(num1);
  const places2 = getDecimalPlaces(num2);
  const factor = Math.pow(10, places1 + places2);
  const int1 = Math.round(num1 * Math.pow(10, places1));
  const int2 = Math.round(num2 * Math.pow(10, places2));
  return (int1 * int2) / factor;
}

/**
 * 精确除法（避免浮点数精度丢失）
 * @param num1 被除数
 * @param num2 除数
 * @returns 精确的商
 * @example
 * divide(0.3, 0.1) // 3
 * divide(10.5, 2.5) // 4.2
 */
export function divide(num1: number, num2: number): number {
  if (num2 === 0) return 0;
  const places1 = getDecimalPlaces(num1);
  const places2 = getDecimalPlaces(num2);
  const int1 = Math.round(num1 * Math.pow(10, places1));
  const int2 = Math.round(num2 * Math.pow(10, places2));
  // 补偿小数位差异：int1/int2 * 10^(places2-places1)
  return multiply(int1 / int2, Math.pow(10, places2 - places1));
}

/**
 * 计算商品总价（单价 * 数量）
 * @param price 单价
 * @param qty 数量
 * @returns 精确的总价
 */
export function calcItemTotal(price: number, qty: number): number {
  return multiply(price, qty);
}

/**
 * 累加多个金额
 * @param amounts 金额数组
 * @returns 精确的总和
 */
export function sumAmounts(amounts: number[]): number {
  return amounts.reduce((sum, amount) => add(sum, amount), 0);
}

/**
 * 安全的金额格式化（解决 toFixed 四舍五入精度问题），展示时不补 0
 * @param num 金额数值
 * @param decimals 最多保留的小数位数，默认 2
 * @returns 格式化后的字符串
 * @example
 * formatPrice(1.005) // "1.01"（原生 toFixed 会返回 "1.00"）
 * formatPrice(0.1 + 0.2) // "0.3"
 * formatPrice(111) // "111"
 * formatPrice(111.1) // "111.1"
 * formatPrice(undefined) // "0"
 */
export function formatPrice(
  num: number | string | undefined | null,
  decimals: number = 2,
): string {
  const value = typeof num === "string" ? Number(num) : num;
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return "0";
  }

  const rounded = Number(
    Math.round(parseFloat(`${value}e${decimals}`)) + `e-${decimals}`,
  );
  const fixed = rounded.toFixed(decimals);
  if (!fixed.includes(".")) return fixed;
  return fixed.replace(/\.?0+$/, "");
}

/**
 * 向上取整到指定小数位（常用于运费向上取整到分）
 * @param num 数值
 * @param decimals 小数位数，默认 2
 * @returns 向上取整后的数值
 * @example
 * ceilPrice(1.001) // 1.01
 * ceilPrice(1.009) // 1.01
 */
export function ceilPrice(num: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.ceil(multiply(num, factor)) / factor;
}

// 业务级别错误白名单- 在公共请求中特殊处理
export const BUSINESS_ERROR_CODES: Set<number> = new Set([
  10401, // 未登录
  100002, // 游客登录
  10002, // 账号被封禁
  100001, // 审核限制
]);

// 错误码对应提示，直接提示错误信息
export const ERROR_MESSAGE_MAP: Record<number | string, string> = {
  ERR_NETWORK: "网络连接异常，请检查网络设置",
  ERR_TIMEOUT: "请求超时，请稍后重试",
  500: "服务器繁忙，请稍后重试",
  502: "网关错误，请稍后重试",
  503: "服务不可用，请稍后重试",
  504: "网关超时，请稍后重试",
};
